import { z } from 'zod';
import { LEDGER_FRAMEWORKS, TAX_CODES } from './tax.js';
/**
 * Kontenplan (KON-162). Konten stehen in Kategorien und Unterkategorien, die der Mandant selbst anlegt und ordnet. Die oberste Kategorie legt die Art fest (Vermögen,
 * Verbindlichkeiten, Eigenkapital und Privat, Erlöse, Aufwand, neutral), die Unterkategorien und ihre Konten erben sie. Der Plan beginnt mit einem Vorschlag, der zum
 * gewählten Kontenrahmen passt (Standard für kleine Einzelunternehmen, SKR03 oder SKR04, siehe chart-templates.ts). Daraus wird ein eigener Plan, den der Mandant frei ändert.
 */
export const CHART_KINDS = ['asset', 'liability', 'equity', 'revenue', 'expense', 'neutral'];
export const CHART_KIND_LABELS = {
    asset: 'Vermögen',
    liability: 'Verbindlichkeiten',
    equity: 'Eigenkapital und Privat',
    revenue: 'Erlöse',
    expense: 'Aufwand',
    neutral: 'Neutral',
};
export const CHART_FRAMEWORK_LABELS = {
    none: 'Wirkia Standard (kleines Einzelunternehmen)',
    SKR03: 'SKR03 (Auszug)',
    SKR04: 'SKR04 (Auszug)',
};
export const CHART_FRAMEWORK_HINTS = {
    none: 'Ein übersichtlicher Plan mit rund 50 Konten für ein kleines Einzelunternehmen, nach Einnahmen-Überschuss-Rechnung. Gut, wenn Sie selbst buchen oder Ihr Steuerberater keinen bestimmten Rahmen verlangt.',
    SKR03: 'Der Standardkontenrahmen 03 nach dem Prozessgliederungsprinzip, bei Kleinbetrieben und Freiberuflern verbreitet. Wirkia liefert einen Auszug der gängigen Konten, den vollständigen Rahmen können Sie per CSV importieren.',
    SKR04: 'Der Standardkontenrahmen 04 nach dem Abschlussgliederungsprinzip, bei bilanzierenden Betrieben verbreitet. Wirkia liefert einen Auszug der gängigen Konten, den vollständigen Rahmen können Sie per CSV importieren.',
};
const name = (label) => z.string().trim().min(1, `${label} fehlt`).max(100, 'Höchstens 100 Zeichen');
const code = z.string().trim().max(10, 'Höchstens 10 Zeichen').nullable().optional().transform((v) => (v ? v : null));
const accountNumber = z.string().trim().regex(/^[0-9]{1,8}$/, 'Nur Ziffern, höchstens 8 Stellen');
const taxCodeCheck = (v) => v == null || v === '' || TAX_CODES.some((t) => t.code === v);
const taxCode = z.string().nullable().optional().refine(taxCodeCheck, 'Unbekannter Steuerschlüssel').transform((v) => (v ? v : null));
/** Beim Ändern: nicht angegeben bleibt unverändert (undefined), leer löscht */
const patchTaxCode = z.string().nullable().optional().refine(taxCodeCheck, 'Unbekannter Steuerschlüssel').transform((v) => (v === undefined ? undefined : v ? v : null));
const patchCode = z.string().trim().max(10, 'Höchstens 10 Zeichen').nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null));
/** Neue Kategorie. Ohne `parentId` ist sie eine oberste Kategorie und braucht die Art, Unterkategorien erben sie. */
export const createCategoryInput = z
    .object({
    parentId: z.uuid('Ungültige Kategorie').nullable().optional(),
    code,
    name: name('Name'),
    kind: z.enum(CHART_KINDS, 'Bitte eine Art wählen').optional(),
})
    .strict()
    .superRefine((v, ctx) => {
    if (!v.parentId && !v.kind)
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'Bitte die Art wählen (zum Beispiel Erlöse oder Aufwand)' });
    if (v.parentId && v.kind)
        ctx.addIssue({ code: 'custom', path: ['kind'], message: 'Eine Unterkategorie erbt die Art der obersten Kategorie' });
});
export const updateCategoryInput = z
    .object({
    code: patchCode,
    name: name('Name').optional(),
    kind: z.enum(CHART_KINDS).optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** Eine Kategorie verschieben: unter eine andere (oder nach oben, `parentId` null) und eine Stelle hoch oder runter innerhalb ihrer Ebene */
export const moveCategoryInput = z
    .object({
    parentId: z.uuid('Ungültige Kategorie').nullable().optional(),
    /** Beim Verschieben nach oben (zur obersten Kategorie) braucht sie eine Art */
    kind: z.enum(CHART_KINDS).optional(),
    direction: z.enum(['up', 'down']).optional(),
})
    .strict()
    .refine((v) => v.parentId !== undefined || v.direction !== undefined, 'Bitte angeben, wohin die Kategorie soll');
export const createChartAccountInput = z
    .object({
    number: accountNumber,
    name: name('Bezeichnung'),
    description: z.string().trim().max(500).nullable().optional().transform((v) => (v ? v : null)),
    categoryId: z.uuid('Bitte eine Kategorie wählen'),
    defaultTaxCode: taxCode,
    active: z.boolean().optional(),
})
    .strict();
export const updateChartAccountInput = z
    .object({
    number: accountNumber.optional(),
    name: name('Bezeichnung').optional(),
    description: z.string().trim().max(500).nullable().optional().transform((v) => (v === undefined ? undefined : v ? v : null)),
    categoryId: z.uuid('Bitte eine Kategorie wählen').optional(),
    defaultTaxCode: patchTaxCode,
    active: z.boolean().optional(),
})
    .strict()
    .refine((v) => Object.values(v).some((x) => x !== undefined), 'Keine Änderung angegeben');
/** CSV mit den Spalten Kontonummer;Bezeichnung;Kategorie;Beschreibung;Steuerschlüssel;Aktiv (Trennzeichen Semikolon oder Komma, erste Zeile Kopf) */
export const importChartInput = z.object({ csv: z.string().min(1, 'Die Datei ist leer').max(2_000_000, 'Die Datei ist zu groß') }).strict();
export const switchChartInput = z
    .object({
    framework: z.enum(LEDGER_FRAMEWORKS),
    /** Eigene Konten (nicht aus dem Vorschlag) behalten. Verwendete Konten bleiben immer. */
    keepCustom: z.boolean().default(true),
    /** Konten je Steuerschlüssel (Erlös- und Steuerkonto) auf die Vorgaben des neuen Rahmens setzen */
    resetTaxAccounts: z.boolean().default(true),
})
    .strict();
// ---------------------------------------------------------------------------
// Finanzreport
// ---------------------------------------------------------------------------
export const REPORT_BASES = ['invoice', 'payment'];
export const REPORT_BASIS_LABELS = {
    invoice: 'Nach Rechnungsdatum (Sollversteuerung)',
    payment: 'Nach Zahlungsdatum (Istversteuerung)',
};
export const reportQuery = z
    .object({
    from: z.iso.date('Bitte ein Datum angeben'),
    to: z.iso.date('Bitte ein Datum angeben'),
    basis: z.enum(REPORT_BASES).optional(),
    format: z.enum(['json', 'csv', 'pdf']).default('json'),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.to < v.from)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Das Ende liegt vor dem Beginn' });
    if (Date.parse(`${v.to}T00:00:00Z`) - Date.parse(`${v.from}T00:00:00Z`) > 366 * 86_400_000 * 5)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Höchstens fünf Jahre auf einmal' });
});
/** Summen je Konto für die Spalte „Summe“ im Kontenplan: ein Jahr, ohne Jahr alles seit Beginn */
export const chartTotalsQuery = z.object({ year: z.coerce.number().int().min(2000).max(2100).optional() }).strict();
// ---------------------------------------------------------------------------
// Sachposten: die einzelnen Posten hinter den Summen des Finanzreports
// ---------------------------------------------------------------------------
export const POSTING_KINDS = ['revenue', 'expense'];
export const POSTING_KIND_LABELS = { revenue: 'Erlös', expense: 'Aufwand' };
/** `none` als Konto steht für Posten ohne Konto (kein Erlöskonto je Steuerschlüssel, kein Sachkonto an der Rechnung) */
export const NO_ACCOUNT = 'none';
export const listPostingsQuery = z
    .object({
    from: z.iso.date('Bitte ein Datum angeben'),
    to: z.iso.date('Bitte ein Datum angeben'),
    basis: z.enum(REPORT_BASES).optional(),
    /** Kontonummer oder `none` */
    account: z.string().trim().max(20).optional(),
    kind: z.enum(POSTING_KINDS).optional(),
    q: z.string().trim().max(100).optional(),
    page: z.coerce.number().int().min(1).default(1),
    perPage: z.coerce.number().int().min(1).max(200).default(50),
    sort: z.enum(['date', '-date', 'amount', '-amount', 'account', 'document']).default('-date'),
    format: z.enum(['json', 'csv']).default('json'),
})
    .strict()
    .superRefine((v, ctx) => {
    if (v.to < v.from)
        ctx.addIssue({ code: 'custom', path: ['to'], message: 'Das Ende liegt vor dem Beginn' });
});
//# sourceMappingURL=chart.js.map